import { useEffect, useState } from 'react';
import { ORDER_FORM_URL, OrderData } from '@/pages/home/components/OrderForm';

interface Props {
  onClose: () => void;
  orderData: OrderData | null;
}

const DiamondBorder = () => (
  <div
    className="h-5 w-full flex-shrink-0"
    style={{
      background: `repeating-linear-gradient(45deg, #4a4a4a 0px, #4a4a4a 5px, #3a3a3a 5px, #3a3a3a 10px)`,
    }}
  />
);

const ConfirmationModal = ({ onClose, orderData }: Props) => {
  const [step, setStep] = useState<'summary' | 'venmo'>('summary');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const postOrder = async () => {
    if (!orderData) return;
    const body = new URLSearchParams();

    body.append('name', orderData.name);
    body.append('phone', orderData.phone);
    body.append('logistics', orderData.logistics);
    body.append('specialRequests', orderData.specialRequests);

    // Regular cans
    orderData.regularCans.forEach((can, i) => {
      const p = `regular_can_${i + 1}`;
      body.append(`${p}_logoStyle`, can.logoStyle || 'none');
      body.append(`${p}_leftNumber`, can.leftNumber);
      body.append(`${p}_rightNumber`, can.rightNumber);
    });
    body.append('regular_can_count', String(orderData.regularCans.length));

    // Trash cans
    orderData.trashCans.forEach((can, i) => {
      const p = `trash_can_${i + 1}`;
      body.append(`${p}_logoStyle`, can.logoStyle || 'none');
      body.append(`${p}_leftNumber`, can.leftNumber);
      body.append(`${p}_rightNumber`, can.rightNumber);
    });
    body.append('trash_can_count', String(orderData.trashCans.length));

    // Pouches
    orderData.pouchEntries.forEach((entry, i) => {
      const p = `pouch_${i + 1}`;
      body.append(`${p}_brand`, entry.brand);
      body.append(`${p}_flavor`, entry.flavor || 'Any');
      body.append(`${p}_strength`, entry.strength);
      body.append(`${p}_quantity`, String(entry.quantity));
    });
    const totalPacks = orderData.pouchEntries.reduce((s, e) => s + e.quantity, 0);
    body.append('pouch_total_packs', String(totalPacks));

    body.append('totalDue', `$${orderData.totalAmount.toFixed(2)}`);

    try {
      await fetch(ORDER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // proceed regardless — don't block the user
    }
  };

  const handleProceedToPayment = async () => {
    setSubmitting(true);
    await postOrder();
    setSubmitting(false);
    setStep('venmo');
  };

  const total = orderData?.totalAmount ?? 0;
  const regularCans = orderData?.regularCans ?? [];
  const trashCans = orderData?.trashCans ?? [];
  const pouchEntries = orderData?.pouchEntries ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.88)' }}
      onClick={(e) => { if (e.target === e.currentTarget && step === 'venmo') onClose(); }}
    >
      <div
        className="relative w-full max-w-lg border-2 overflow-hidden overflow-y-auto transition-all duration-300"
        style={{
          background: '#1a1a1a',
          borderColor: step === 'venmo' ? '#D4FF00' : '#CE1126',
          boxShadow: step === 'venmo' ? '0 0 60px rgba(212,255,0,0.2)' : '0 0 60px rgba(206,17,38,0.3)',
          maxHeight: '90vh',
        }}
      >
        <DiamondBorder />

        {/* ── STEP 1: ORDER SUMMARY ── */}
        {step === 'summary' && (
          <div className="p-8 md:p-10">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 flex items-center justify-center rounded-full" style={{ background: '#D4FF00' }}>
                <i className="ri-file-list-3-line text-4xl text-black" />
              </div>
            </div>

            <h2
              className="text-3xl md:text-4xl text-white text-center uppercase tracking-widest mb-1"
              style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
            >
              ORDER SUMMARY
            </h2>
            <p className="text-[#888] font-mono text-xs text-center tracking-widest mb-6">
              REVIEW YOUR ORDER BEFORE PROCEEDING
            </p>

            <div className="h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/40 to-transparent mb-6" />

            <div className="bg-[#111] border border-[#2a2a2a] p-5 mb-6">
              <div className="space-y-3 font-mono text-sm">

                {/* Member */}
                <div className="flex justify-between items-center">
                  <span className="text-[#888]">MEMBER:</span>
                  <span className="text-white">{orderData?.name || '—'}</span>
                </div>

                {/* Regular Cans */}
                {regularCans.length > 0 && (
                  <>
                    <div className="h-px bg-[#2a2a2a]" />
                    <p className="text-[#CE1126] text-xs tracking-[0.25em] uppercase">Regular Cans</p>
                    {regularCans.map((can, i) => (
                      <div key={can.id} className="flex justify-between items-center">
                        <span className="text-[#888]">
                          Regular Can #{i + 1}{can.logoStyle ? ` · ${can.logoStyle}` : ''}
                          {can.leftNumber !== 'none' || can.rightNumber !== 'none'
                            ? ` (${can.leftNumber !== 'none' ? can.leftNumber : '—'} / ${can.rightNumber !== 'none' ? can.rightNumber : '—'})`
                            : ''}
                        </span>
                        <span className="text-white flex-shrink-0 ml-2">$50.00</span>
                      </div>
                    ))}
                  </>
                )}

                {/* Trash Cans */}
                {trashCans.length > 0 && (
                  <>
                    <div className="h-px bg-[#2a2a2a]" />
                    <p className="text-[#C0C0C0] text-xs tracking-[0.25em] uppercase">Trash Cans</p>
                    {trashCans.map((can, i) => (
                      <div key={can.id} className="flex justify-between items-center">
                        <span className="text-[#888]">
                          Trash Can #{i + 1}{can.logoStyle ? ` · ${can.logoStyle}` : ''}
                          {can.leftNumber !== 'none' || can.rightNumber !== 'none'
                            ? ` (${can.leftNumber !== 'none' ? can.leftNumber : '—'} / ${can.rightNumber !== 'none' ? can.rightNumber : '—'})`
                            : ''}
                        </span>
                        <span className="text-white flex-shrink-0 ml-2">$75.00</span>
                      </div>
                    ))}
                  </>
                )}

                {/* Pouches */}
                {pouchEntries.length > 0 && (
                  <>
                    <div className="h-px bg-[#2a2a2a]" />
                    <p className="text-[#D4FF00] text-xs tracking-[0.25em] uppercase">Pouches</p>
                    {pouchEntries.map((entry, i) => {
                      const label = [entry.brand, entry.flavor, entry.strength]
                        .filter(Boolean)
                        .join(' ') || `Flavor #${i + 1}`;
                      return (
                        <div key={entry.id} className="flex justify-between items-start gap-2">
                          <span className="text-[#888] leading-snug">{label} ×{entry.quantity}</span>
                          <span className="text-[#D4FF00] flex-shrink-0">${(entry.quantity * 7.5).toFixed(2)}</span>
                        </div>
                      );
                    })}
                  </>
                )}

                <div className="h-px bg-[#2a2a2a]" />

                {/* Total */}
                <div className="flex justify-between items-center">
                  <span className="text-[#888]">TOTAL DUE:</span>
                  <span
                    className="text-3xl font-bold text-white"
                    style={{ fontFamily: '"Courier New", monospace' }}
                  >
                    ${total.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/40 to-transparent mb-6" />

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-4 bg-[#111] border border-[#3a3a3a] text-[#aaa] font-black uppercase tracking-[0.3em] text-base transition-all duration-200 hover:border-[#555] hover:text-white active:scale-95 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
              >
                <i className="ri-arrow-left-line mr-2" />
                GO BACK
              </button>
              <button
                onClick={handleProceedToPayment}
                disabled={submitting}
                className="flex-1 py-4 bg-[#CE1126] border border-[#C0C0C0] text-white font-black uppercase tracking-[0.3em] text-base transition-all duration-200 hover:bg-[#a50e1f] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2" />
                    TRANSMITTING...
                  </>
                ) : (
                  <>
                    PROCEED <br />TO PAYMENT
                    <i className="ri-arrow-right-line ml-2" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: VENMO PAYMENT ── */}
        {step === 'venmo' && (
          <div className="p-8 md:p-10">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 flex items-center justify-center rounded-full" style={{ background: '#D4FF00' }}>
                <i className="ri-check-double-line text-4xl text-black font-black" />
              </div>
            </div>

            <h2
              className="text-3xl md:text-4xl text-white text-center uppercase tracking-widest mb-1"
              style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
            >
              ORDER DISPATCHED
            </h2>
            <p className="text-[#888] font-mono text-xs text-center tracking-widest mb-6">
              TRANSMISSION RECEIVED — SEND PAYMENT TO CONFIRM
            </p>

            <div className="h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/40 to-transparent mb-6" />

            <div className="flex flex-col sm:flex-row items-center gap-6 bg-[#111] border border-[#D4FF00]/30 p-5 mb-6">
              <div className="flex-shrink-0">
                <div className="w-36 h-36 bg-white p-1.5 rounded-sm">
                  <img
                    src="https://static.readdy.ai/image/9e0ec1f08df5eac0f8e6ee60a23adb36/9a7d0f79b2cd03cb39f266eb97cd6004.jpeg"
                    alt="Venmo QR code for @shaunmills"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-[#888] font-mono text-xs text-center mt-2 tracking-widest">SCAN TO PAY</p>
              </div>
              <div className="text-center sm:text-left">
                <p className="text-[#D4FF00] font-mono text-xs tracking-[0.3em] uppercase mb-1">Venmo</p>
                <p
                  className="text-white text-2xl font-black tracking-widest mb-1"
                  style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
                >
                  @shaunmills
                </p>
                <p className="text-[#888] font-mono text-sm mb-1">Shaun Mills</p>
                <p
                  className="text-[#D4FF00] text-xl font-black mb-3"
                  style={{ fontFamily: '"Courier New", monospace' }}
                >
                  ${total.toFixed(2)}
                </p>
                <p className="text-[#aaa] font-mono text-xs leading-relaxed">
                  Include your name in the<br />Venmo payment note.
                </p>
              </div>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/40 to-transparent mb-6" />

            <div className="text-center mb-8">
              <p
                className="text-white text-sm uppercase tracking-widest mb-2"
                style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
              >
                QUESTIONS? CONTACT SHAUN
              </p>
              <a
                href="tel:9258583739"
                className="text-[#D4FF00] font-mono text-xl tracking-widest hover:text-white transition-colors duration-200 cursor-pointer"
              >
                (925) 858-3739
              </a>
            </div>

            <button
              onClick={onClose}
              className="w-full py-4 bg-[#111] border border-[#3a3a3a] text-[#aaa] font-black uppercase tracking-[0.3em] text-base transition-all duration-200 hover:border-[#555] hover:text-white active:scale-95 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
            >
              CLOSE
            </button>
          </div>
        )}

        <DiamondBorder />
      </div>
    </div>
  );
};

export default ConfirmationModal;
